import {
  ContentTypeEnum,
  contentTypeUrlValue,
  HorizontalThumbnailEnum,
  LangEnum,
  OSFamilyEnum,
  PlatformEnum,
  ShowFormatEnum,
  SourcePagesEnum,
  WidgetTypeEnum,
} from "@/constants/constants";
import { Thumbnail } from "@/types/common.types";
import {
  MappedSelectedPeripheral,
  SelectedPeripheral,
} from "@/types/contentDetails.types";
import * as HomeLayoutInterface from "@/types/home.types";
import {
  ContentListingItem,
  HomeLayoutItemInterface,
  HomeRowDetailData,
  MappedContinueWatching,
} from "@/types/home.types";
import { CountryListItem } from "@/types/user.types";
import {
  captureError,
  generateContentDetailURL,
  generateURLSlug,
  generateWatchPageURL,
  getContentTypeUrlValue,
  getCurrentLang,
  getThumbnailsFromObject,
  getTrailerURLsFromSelectedPeripheral,
  isServer,
  isTvEnv,
} from "@/utils/common.helpers";
import { API_ROUTES } from "../apiRoutes";
import { BaseAPIClient } from "../BaseAPIClient";
import { REQUEST_METHODS } from "../configs.apiClient";
import { HttpClient } from "../HttpClient";
import { getOrCreateDeviceId } from "@/utils/deviceId";
export class HomeAPIClient extends BaseAPIClient {
  constructor(httpClient: HttpClient) {
    super(httpClient);
  }

  // T means request response and K means objects of which array is returned as list
  private async getHomeList<
    T extends { data?: any },
    K extends {
      format?: ShowFormatEnum;
      description: any;
      selectedPeripheral?: SelectedPeripheral;
      _id: number;
      thumbnail: Thumbnail;
      title: string;
      contentUrl: string;
      slug: string;
      overlayTag?: string;
      duration?: number;
      genreList?: { id: number; name: string }[];
      descriptorTags?: { id: number; name: string }[];
      releaseDate?: string;
      startDate?: string;
      createdAt?: string;
    },
  >({
    data,
    key,
    nestedKey,
    thumbnailSize,
    typeKey,
    resultKey,
    additionalResultKeys,
    disableFilter = true,
    horizontalThumbnailRatio = HorizontalThumbnailEnum.STANDARD,
  }: {
    data: {
      cache?: boolean;
      endpoint: string;
      options: RequestInit;
      queryParams?: Record<string, string | number | boolean>;
      body?: Record<string, string | number | boolean | object>;
      addToken?: boolean;
      lang: string;
      dialect: string;
      addDayType?: boolean;
    };
    key?: keyof T["data"];
    typeKey?: keyof K;
    nestedKey?: string;
    thumbnailSize: "large" | "small";
    resultKey?: string;
    additionalResultKeys?: {
      key: keyof T["data"];
      name: string;
    }[];
    disableFilter?: boolean;
    horizontalThumbnailRatio?: HorizontalThumbnailEnum;
  }) {
    const [response, englishResponse] = await this.callApiWithEnglish<T>(data);

    const filteredData =
      disableFilter === false && typeKey
        ? nestedKey
          ? response?.data?.[key]?.[nestedKey]?.filter(
              (item: K) => item[typeKey] === "show",
            )
          : response?.data?.[key]?.filter((item: K) => item[typeKey] === "show")
        : key
          ? nestedKey
            ? response.data?.[key][nestedKey]
            : response.data?.[key]
          : response.data;

    const englishListing = key
      ? nestedKey
        ? englishResponse?.data?.[key][nestedKey]
        : englishResponse?.data?.[key]
      : englishResponse.data;

    const result =
      filteredData
        .filter((item: K) => item._id)
        .map((item: K) => {
          const itemType = typeKey ? item[typeKey] : "show";

          const thumbnails = getThumbnailsFromObject(
            item.thumbnail,
            itemType === ContentTypeEnum.SHOW,
            thumbnailSize,
            horizontalThumbnailRatio,
          );
          const englishShow = englishListing.find((s: K) => {
            if (s.slug && item.slug) return s.slug === item.slug;
            return s._id === item._id;
          });
          const slug = generateURLSlug({
            contentName: englishShow?.title || "",
            fallbackSlug: englishShow?.slug,
            contentId: item._id,
          });
          const contentTypeUrlValue = getContentTypeUrlValue(
            itemType as ContentTypeEnum,
            item.format,
          );

          const contentUrl = generateContentDetailURL(
            data.lang as LangEnum,
            data.dialect,
            contentTypeUrlValue,
            slug,
          );

          const watchPageUrl = generateWatchPageURL(
            data.lang as LangEnum,
            data.dialect,
            contentTypeUrlValue,
            slug,
            null,
            false,
          );

          const selectedPeripheral = getTrailerURLsFromSelectedPeripheral({
            selectedPeripheral: item.selectedPeripheral as SelectedPeripheral,
            contentType: itemType as ContentTypeEnum,
            slug: item.slug,
          });

          return {
            _id: item._id,
            type: itemType,
            title: item.title,
            description: item.description,
            contentUrl,
            slug: item.slug,
            selectedPeripheral,
            watchPageUrl,
            overlayTag: item.overlayTag,
            duration: item.duration,
            genreList: item.genreList,
            descriptorTags: item.descriptorTags,
            releaseDate: item.releaseDate || item.startDate || item.createdAt,
            ...thumbnails,
          };
        }) || [];

    if (!resultKey) {
      return result;
    }

    const finalResult = resultKey
      ? {
          referralWidgetId: response?.data?.referralWidgetId,
          label: response?.data?.label,
          [resultKey]: result,
        }
      : result;

    if (resultKey && additionalResultKeys) {
      for (const item of additionalResultKeys) {
        finalResult[item.name] = response?.data?.[item.key];
      }
    }

    return finalResult;
  }

  async getHomeLayout(dialect: string) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.home.getHomeLayoutForAnonymousUser;

      const queryParams = {
        isTrialSubscriptionBuildUser: true,
        isRecurringSubscriptionBuild: true,
        appId: "in.stage",
      };

      const response =
        await this.httpClient.request<HomeLayoutInterface.HomeLayoutBodyInterface>(
          {
            endpoint,
            options: {
              method,
            },
            queryParams,
            // TODO: make this public
            addToken: true,
            dialect,
            addDayType: true,
          },
        );
      return response.data;
    } catch (error) {
      console.error(
        "Error in HomeAPIClient getHomeLayoutForAnonymousUser",
        error,
      );
      throw captureError(error);
    }
  }

  async getTrendingContent({
    dialect,
    lang,
  }: {
    dialect: string;
    lang?: string;
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.home.getTrendingContent;
      const finalLang = lang ?? (await getCurrentLang());

      const trendingContentRowData = await this.getHomeList<
        HomeLayoutInterface.TrendingContent,
        HomeLayoutInterface.ContentListingItem
      >({
        data: {
          endpoint,
          options: {
            method,
          },
          addToken: false,
          dialect,
          lang: finalLang,
        },
        key: "trendingContent",
        typeKey: "contentType",
        thumbnailSize: "small",
        resultKey: "trendingContent",
      });

      return trendingContentRowData;
    } catch (error) {
      console.error("Error in HomeAPIClient getTrendingContent", error);
      captureError(error);
      return {
        label: "",
        trendingContent: [],
      };
    }
  }

  async getContinueWatching({
    dialect,
    widgetData,
    token,
  }: {
    dialect: string;
    widgetData: HomeLayoutItemInterface;
    token?: string;
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      // FIXME: Using the URL from widgetData.url causes a preflight error due to the 'dialect' header not being allowed by Access-Control-Allow-Headers in the preflight response.
      const endpoint = API_ROUTES.home.continueWatchingTray;
      const lang = await getCurrentLang();

      const response =
        await this.httpClient.paatarServerRequest<HomeLayoutInterface.ContinueWatchingWidgetRes>(
          {
            endpoint,
            options: {
              method,
            },
            token,
            addToken: true,
          },
        );

      // Handle empty response (Content-Length: 0)
      if (!response || response === null) {
        return {
          label: "",
          referralWidgetId: 0,
          continueWatching: [],
          updatedAt: Date.now(),
        } as MappedContinueWatching;
      }

      if (!response.status) {
        throw new Error(response.errorMessage);
      }

      return {
        label: response.data.trayLabel,
        referralWidgetId: response.data.trayID,
        continueWatching: response.data.items.map((item) => {
          const isShow = item.contentType === ContentTypeEnum.SHOW;
          const episodeNumber =
            isShow && item.metadata?.episodeNumber
              ? item.metadata?.episodeNumber
              : null;

          const seasonId =
            isShow && item.metadata?.seasonID ? item.metadata?.seasonID : null;

          const contentTypeUrlValue = getContentTypeUrlValue(
            item.contentType as ContentTypeEnum,
            item.metadata?.showFormat,
          );

          const contentUrl = generateWatchPageURL(
            lang as LangEnum,
            dialect,
            contentTypeUrlValue,
            item.slugDisplay,
            episodeNumber,
            false,
            seasonId
              ? {
                  seasonId: seasonId.toString(),
                }
              : {},
          );

          return {
            _id: item.contentID,
            verticalThumbnail: item.thumbnail.vertical.url,
            horizontalThumbnail: item.thumbnail.horizontal.url,
            squareThumbnail: "",
            type: item.contentType,
            title: item.displayTitle,
            contentUrl,
            consumedPercentage: item.consumedPercentage,
            consumedDuration: item.consumedDuration,
            totalDuration: item.totalDuration,
            showId: item.metadata?.showID,
            slug: item.slug,
            formattedRemainingDuration: item.formattedRemainingDuration,
            tag: item.tag,
          };
        }),
        updatedAt: response.data.updatedAt,
      } as MappedContinueWatching;
    } catch (error) {
      console.error("Error in HomeAPIClient getContinueWatching", error);

      captureError(error);
      return {
        error: true,
        errorMsg:
          (error as Error)?.message || `Failed to load continue watching data`,
      };
    }
  }
  async generateTvCode(deviceId: string) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.user.generateTvCode;
      const dialect = "haryanvi";
      const lang = "hi";

      const queryParams: Record<string, string> = {
        deviceId,
        platform: PlatformEnum.TV,
        os: OSFamilyEnum.ANDROID,
      };

      const response = await this.httpClient.request<{
        responseCode: number;
        data?: any;
      }>({
        endpoint,
        options: {
          method,
        },
        queryParams,
        dialect,
        lang,
        body: {
          tvDeviceId: deviceId,
        },
      });
      return response;
    } catch (error) {
      console.error("Error in UserAPIClient generateTvCode", error);
      throw captureError(error);
    }
  }

  async verifyTvLoginCode({
    tvCode,
    dialect,
  }: {
    tvCode: string;
    dialect: string;
  }) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.user.verifyTvCode;
      const queryParams = {
        isTrialSubscriptionBuildUser: true,
        isRecurringSubscriptionBuild: true,
      };

      const response = await this.httpClient.request<{ responseCode: number }>({
        endpoint,
        options: {
          method,
        },
        queryParams,
        addToken: true,
        dialect,
        body: {
          tvLoginCode: tvCode,
        },
      });

      if (response.responseCode === 200) return true;
      return false;
    } catch (error) {
      console.error("Error in HomeAPIClient verifyTvCOde", error);
      throw captureError(error);
    }
  }

  async getGenreList({
    endpoint,
    lang,
    dialect,
    sourcePage,
    sourceWidget,
  }: {
    endpoint: string;
    lang: string;
    dialect: string;
    sourcePage?: SourcePagesEnum;
    sourceWidget?: string;
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      const response =
        await this.httpClient.request<HomeLayoutInterface.GenreListBodyInterface>(
          {
            endpoint,
            options: {
              method,
            },
            addToken: true,
            dialect,
            lang,
            cache: true,
          },
        );

      return response.data.genreListing;
    } catch (error) {
      console.error("Error in HomeAPIClient getGenreList", error);

      captureError(error);
      return {
        error: true,
        errorMsg: (error as Error)?.message || `Failed to load genre list data`,
      };
    }
  }

  async getDynamicWidgetData<
    // api response
    T extends { responseMessage: string; data: any } = {
      responseMessage: string;
      data: ContentListingItem[];
    },
    // api response array item
    K extends
      HomeLayoutInterface.ContentListingItem = HomeLayoutInterface.ContentListingItem,
  >({
    endpoint,
    lang,
    dialect,
    thumbnailSize = "small",
    addDayType = false,
    apiResKey,
    horizontalThumbnailRatio = HorizontalThumbnailEnum.STANDARD,
    cacheApiResponse,
    type,
  }: {
    endpoint: string;
    lang: string;
    dialect: string;
    thumbnailSize?: "large" | "small";
    apiResKey?: keyof T["data"];
    horizontalThumbnailRatio?: HorizontalThumbnailEnum;
    cacheApiResponse?: boolean;
    addDayType?: boolean;
    type?: "individual" | "show";
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      const finalLang = lang ?? (await getCurrentLang());

      const queryParams: Record<string, string> = {};
      if (type) {
        queryParams.type = type;
      }

      const rowData = await this.getHomeList<T, K>({
        data: {
          endpoint,
          options: {
            method,
          },
          // TODO: remove this
          addToken: true,
          dialect,
          lang: finalLang,
          addDayType,
          cache: cacheApiResponse,
          queryParams:
            Object.keys(queryParams).length > 0 ? queryParams : undefined,
        },
        typeKey: "contentType",
        thumbnailSize,
        disableFilter: true,
        horizontalThumbnailRatio: horizontalThumbnailRatio,
        key: apiResKey,
      });

      return rowData as HomeLayoutInterface.ContentInterface[];
    } catch (error) {
      console.error(
        `Error in HomeAPIClient getDynamicWidget Data -> ${endpoint}`,
        error,
      );
      captureError(error);
      return [
        {
          error: true,
          errorMsg:
            (error as Error)?.message ||
            `Failed to load dynamic widget data: ${endpoint}`,
        },
      ];
    }
  }

  async getHomeRowDetail({
    lang,
    dialect,
    expandedRowKey,
  }: {
    lang: string;
    dialect: string;
    expandedRowKey: string;
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.home.getHomeRowDetail;

      const response = await this.httpClient.request<{
        responseMessage: string;
        data: HomeRowDetailData;
      }>({
        endpoint,
        options: {
          method,
        },
        queryParams: {
          key: expandedRowKey,
        },
        addToken: true,
        dialect,
        lang,
      });

      return response.data;
    } catch (error) {
      console.error("Error in HomeAPIClient getHomeRowDetail", error);

      captureError(error);
      return {
        error: true,
        errorMsg: (error as Error)?.message,
      };
    }
  }

  async fetchContentGridData(
    widgetData: HomeLayoutItemInterface,
    lang: string,
    dialect: string,
  ) {
    let response;
    if (widgetData.widgetType === WidgetTypeEnum.TOP_20) {
      response =
        await this.getDynamicWidgetData<HomeLayoutInterface.Top20ContentDataBodyInterface>(
          {
            dialect,
            lang,
            endpoint: widgetData.url,
            apiResKey: "top20Content",
            cacheApiResponse: true,
          },
        );
    } else {
      response = await this.getDynamicWidgetData({
        lang,
        dialect,
        endpoint: widgetData.url,
        cacheApiResponse: true,
      });
    }
    return response;
  }

  async fetchPlatterData(
    widgetData: HomeLayoutItemInterface,
    lang: string,
    dialect: string,
    type?: "individual" | "show",
  ) {
    let response =
      await this.getDynamicWidgetData<HomeLayoutInterface.PlatterDataBodyInterface>(
        {
          dialect,
          endpoint: widgetData.url,
          lang,
          addDayType: true,
          thumbnailSize: "large",
          apiResKey: "platterListing",
          horizontalThumbnailRatio: isTvEnv()
            ? HorizontalThumbnailEnum.WIDE_BANNER
            : HorizontalThumbnailEnum.STANDARD,
          cacheApiResponse: true,
          type,
        },
      );

    if ("error" in response && isTvEnv()) {
      // remove this fallback once we know how we can focus another row if platter data is not available
      response =
        await this.getDynamicWidgetData<HomeLayoutInterface.PlatterDataBodyInterface>(
          {
            dialect,
            endpoint: API_ROUTES.home.getPlatterData,
            lang,
            addDayType: true,
            thumbnailSize: "large",
            apiResKey: "platterListing",
            horizontalThumbnailRatio: isTvEnv()
              ? HorizontalThumbnailEnum.WIDE_BANNER
              : HorizontalThumbnailEnum.STANDARD,
            cacheApiResponse: !type, // Don't cache when filtering by type
            type,
          },
        );
    }
    return response;
  }

  async getCountryList(): Promise<CountryListItem[]> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.home.getCountryList;

      const response = await this.httpClient.request<{
        responseMessage: string;
        data: { countryList: CountryListItem[] };
      }>({
        endpoint,
        options: { method },
        addToken: false,
      });

      return response.data.countryList;
    } catch (error) {
      console.error("Error in HomeAPIClient getCountryList", error);
      captureError(error);
      return [];
    }
  }
}
